import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Eye, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { useApproveManualPayments } from '~entities/payment/api/approve-manual-payments';
import type { FailedPayment } from '~entities/payment/api/failed-payments';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import type { FailedPaymentsTableProps } from './failed-payments-table.types';

const columnHelper = createColumnHelper<FailedPayment>();

const FailedPaymentRetryButton = ({
  invoiceId,
  invoiceDate,
  isRetrying,
  onRetry,
}: {
  invoiceId: string;
  invoiceDate: string;
  isRetrying: boolean;
  onRetry: (invoiceId: string, invoiceDate: string) => void;
}) => {
  const retryDate =
    invoiceDate && invoiceDate !== 'N/A'
      ? invoiceDate.slice(0, 10)
      : new Date().toISOString().slice(0, 10);

  return (
    <Button
      variant='outline'
      size='sm'
      className='shrink-0'
      disabled={isRetrying || !invoiceId}
      onClick={() => {
        onRetry(invoiceId, retryDate);
      }}
    >
      {isRetrying ? (
        <Loader2 className='size-4 animate-spin' />
      ) : (
        <RefreshCw className='size-4' />
      )}
      Retry Payment
    </Button>
  );
};

export function FailedPaymentsTable({
  payments,
  formatCurrency,
  formatDate,
  formatDateTime,
}: FailedPaymentsTableProps) {
  const { mutateAsync: retryFailedPayment } = useApproveManualPayments();
  const [retryingInvoiceId, setRetryingInvoiceId] = useState<string | null>(
    null,
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const handleRetryPayment = useCallback(
    (invoiceId: string, retryDate: string) => {
      setRetryingInvoiceId(invoiceId);
      void retryFailedPayment({
        invoiceId: [invoiceId],
        date: retryDate,
      })
        .catch((error: unknown) => {
          console.error('Failed to retry payment', { invoiceId, error });
        })
        .finally(() => {
          setRetryingInvoiceId(null);
        });
    },
    [retryFailedPayment],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('invoiceId', {
        id: 'Invoice ID',
        header: 'Invoice ID',
        enableSorting: false,
        cell: (info) => (
          <div className='font-mono text-sm'>{info.getValue().slice(-8)}</div>
        ),
      }),
      columnHelper.accessor('failedDate', {
        id: 'Failed Date',
        header: 'Failed Date',
        enableSorting: false,
        cell: (info) => formatDateTime(info.getValue()),
      }),
      columnHelper.accessor('dueDate', {
        id: 'Due Date',
        header: 'Due Date',
        enableSorting: false,
        cell: (info) => {
          const dueDate = info.getValue();
          return (
            <span className={dueDate === 'N/A' ? 'italic text-gray-500' : ''}>
              {formatDate(dueDate)}
            </span>
          );
        },
      }),
      columnHelper.accessor('parentName', {
        id: 'Parent',
        header: 'Parent',
        enableSorting: false,
        cell: (info) => {
          const payment = info.row.original;
          return (
            <div>
              <Link
                to='/family'
                search={{ parentId: payment.parentId }}
                className='capitalize text-blue-600 hover:text-blue-800 hover:underline'
              >
                {info.getValue()}
              </Link>
              {payment.parentEmail ? (
                <div className='break-all text-xs text-gray-500'>
                  {payment.parentEmail}
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('studentName', {
        id: 'Student',
        header: 'Student',
        enableSorting: false,
        cell: (info) => {
          const payment = info.row.original;
          return (
            <div>
              <Link
                to='/students/$studentId/details'
                params={{ studentId: payment.studentId }}
                className='capitalize text-blue-600 hover:text-blue-800 hover:underline'
              >
                {info.getValue()}
              </Link>
              {payment.studentEmail ? (
                <div className='break-all text-xs text-gray-500'>
                  {payment.studentEmail}
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('classChargeName', {
        id: 'Class/Event',
        header: 'Class/Event',
        enableSorting: false,
        cell: (info) => (
          <span className='break-words capitalize'>{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'Status',
        header: 'Status',
        enableSorting: false,
        cell: () => <Badge variant='destructive'>Failed</Badge>,
      }),
      columnHelper.accessor('baseAmount', {
        id: 'Base Amount',
        header: 'Base Amount',
        enableSorting: false,
        cell: (info) => {
          const payment = info.row.original;
          return (
            <>
              <div className='space-y-1 md:hidden'>
                <p className='font-medium'>{formatCurrency(info.getValue())}</p>
                {payment.lineItems.map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className='flex justify-between gap-2 text-xs text-gray-600'
                  >
                    <span className='min-w-0 break-words'>
                      {item.name} × {item.quantity}
                    </span>
                    <span className='shrink-0'>
                      {formatCurrency(Number(item.total))}
                    </span>
                  </div>
                ))}
              </div>
              <div className='hidden md:block'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex cursor-pointer items-center gap-2 font-medium hover:opacity-80'>
                      {formatCurrency(info.getValue())}
                      <Eye className='size-3 opacity-50' />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className='w-80 border border-gray-200 bg-white p-4 shadow-lg'>
                    <div className='space-y-2'>
                      <h4 className='font-medium'>Line Items</h4>
                      <div className='space-y-1'>
                        {payment.lineItems.map((item, index) => (
                          <div
                            key={`${item.name}-${index}`}
                            className='flex justify-between border-b pb-1 text-sm'
                          >
                            <div>
                              <div className='font-medium'>{item.name}</div>
                              <div className='text-xs text-gray-500'>
                                {item.type}
                              </div>
                            </div>
                            <div className='text-right'>
                              <div>
                                {formatCurrency(Number(item.amount))} ×{' '}
                                {item.quantity}
                              </div>
                              <div className='font-medium'>
                                {formatCurrency(Number(item.total))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </>
          );
        },
      }),
      columnHelper.accessor('discountAdjustment', {
        id: 'Discount',
        header: 'Discount',
        enableSorting: false,
        cell: (info) => {
          const discount = info.getValue();
          if (discount > 0) {
            return (
              <span className='text-red-600'>-{formatCurrency(discount)}</span>
            );
          }
          return <span className='text-gray-400'>-</span>;
        },
      }),
      columnHelper.accessor('finalAmount', {
        id: 'Final Amount',
        header: 'Final Amount',
        enableSorting: false,
        cell: (info) => (
          <span className='font-medium text-red-600'>
            {formatCurrency(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('paymentMethod', {
        id: 'Payment Method',
        header: 'Payment Method',
        enableSorting: false,
        cell: (info) => (
          <Badge variant='outline' className='capitalize'>
            {info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('failureReason', {
        id: 'Failure Reason',
        header: 'Failure Reason',
        enableSorting: false,
        cell: (info) => (
          <div
            className='max-w-full break-words text-sm text-red-600 md:max-w-48 md:truncate'
            title={info.getValue()}
          >
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const payment = info.row.original;
          return (
            <FailedPaymentRetryButton
              invoiceId={payment.invoiceId}
              invoiceDate={
                payment.invoiceDate ||
                (payment.dueDate !== 'N/A'
                  ? payment.dueDate
                  : payment.failedDate)
              }
              isRetrying={retryingInvoiceId === payment.invoiceId}
              onRetry={handleRetryPayment}
            />
          );
        },
      }),
    ],
    [
      formatCurrency,
      formatDate,
      formatDateTime,
      handleRetryPayment,
      retryingInvoiceId,
    ],
  );

  return (
    <TooltipProvider>
      <EnrollioTable
        ariaLabel='Failed payments'
        totalLabel='Failed payments'
        filterHeader={(table) => (
          <EnrollioTableSearch
            placeholder='Search parents...'
            value={
              (table.getColumn('Parent')?.getFilterValue() as
                | string
                | undefined) ?? ''
            }
            onChange={(value) => {
              table.getColumn('Parent')?.setFilterValue(value);
            }}
          />
        )}
        toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
        columns={columns}
        data={payments}
        className='min-w-0'
        getRowId={(row) => row.invoiceId}
        sorting={sorting}
        onSortingChange={setSorting}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
    </TooltipProvider>
  );
}
